import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { AutocompleteMultiById } from '..';
import apiService from 'library/api-service';
import { EffortContactItem } from 'library/types';
import { GridRowId } from '@mui/x-data-grid-premium';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCallback, useEffect, useState } from 'react';
import { Box, Button, TextField, FormLabel, DialogTitle, FormControl, DialogContentText } from '@mui/material';
import { LoadingBackdrop, ResponsiveDialog, ResponsiveDialogActions, ResponsiveDialogContent } from 'components';

type UpdateEffortStatusContactFormParams = {
  status: number | '';
  toContacts: number[];
  ccContacts: number[];
};

export type UpdateEffortStatusContactPostFormParams = Omit<UpdateEffortStatusContactFormParams, 'status'> & {
  id: number;
  status: number;
};

type UpdateEffortStatusContactDialogProps = {
  open: boolean;
  effortStatusContactId: GridRowId;
  handleClose: () => void;
  refresh: () => Promise<void>;
  effortContacts: EffortContactItem[];
  translateFunction: (effortStatusType: number) => string | undefined;
};

const UpdateEffortStatusContactDialog = ({
  open,
  effortStatusContactId,
  handleClose: closeDialog,
  refresh,
  effortContacts,
  translateFunction,
}: UpdateEffortStatusContactDialogProps) => {
  // other hooks
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  // useState hooks
  const [loading, setLoading] = useState(false);

  // form validations & form state
  const schema = yup.object({
    status: yup
      .number()
      .typeError(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' }))
      .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
    toContacts: yup
      .array()
      .of(
        yup
          .number()
          .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' }))
      )
      .min(
        1,
        intl.formatMessage({
          id: 'validations.effortContacts.minOne',
          defaultMessage: 'En az 1 efor muhatabı seçilmelidir.',
        })
      )
      .required(),
  });

  const { reset, control, handleSubmit, setValue, getValues } = useForm<UpdateEffortStatusContactFormParams>({
    defaultValues: { status: '', toContacts: [], ccContacts: [] },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    // @ts-ignore
    resolver: yupResolver(schema),
    shouldFocusError: true,
  });

  // sync helper functions
  const handleClose = () => {
    closeDialog();
    reset();
  };

  // async helper functions
  const onSubmit = async (data: UpdateEffortStatusContactFormParams) => {
    const postData: UpdateEffortStatusContactPostFormParams = {
      ...data,
      id: effortStatusContactId as number,
      status: Number(data.status),
    };

    try {
      setLoading(true);

      const response = await apiService.updateEffortStatusContact(postData);

      if (response.isSuccess) {
        enqueueSnackbar(
          <FormattedMessage
            id="effortStatusContacts.updateEffortStatusContactCompleted"
            defaultMessage="Efor Statü Muhatabı düzenleme işlemi başarıyla tamamlandı."
          />,
          { variant: 'success' }
        );
        handleClose();
        refresh();
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const getSpecificEffortStatusContact = useCallback(async () => {
    try {
      setLoading(true);

      const response = await apiService.getSpecificEffortStatusContact(effortStatusContactId as number);

      if (response.isSuccess) {
        setValue('status', response.data.status);
        setValue('toContacts', response.data.toContacts);
        setValue('ccContacts', response.data.ccContacts);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, [effortStatusContactId, setValue]);

  // useEffect hooks
  useEffect(() => {
    if (open && effortStatusContactId) {
      getSpecificEffortStatusContact();
    }
  }, [open, effortStatusContactId, getSpecificEffortStatusContact]);

  return (
    <ResponsiveDialog maxWidth="lg" open={open} onClose={handleClose}>
      <DialogTitle>
        <FormattedMessage
          id="effortStatusContacts.updateEffortStatusContact"
          defaultMessage="Efor Statü Muhatabı Düzenle"
        />
      </DialogTitle>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <ResponsiveDialogContent sx={{ display: 'grid' }}>
          <DialogContentText>
            <FormattedMessage
              id="effortStatusContacts.updateEffortStatusContactText"
              defaultMessage="İlgili alanları değiştirip devam ederek seçili Efor Statü Muhatabı için düzenleme yapabilirsiniz."
            />
          </DialogContentText>
          <FormControl>
            <FormLabel htmlFor="status">
              <FormattedMessage id="effortTracing.status" defaultMessage="Statü" />
            </FormLabel>
            <TextField
              value={getValues('status') !== '' ? translateFunction(getValues('status') as number) : ''}
              fullWidth
            />
          </FormControl>
          <FormControl>
            <FormLabel htmlFor="to">TO</FormLabel>
            <Controller
              name="toContacts"
              control={control}
              render={({ field, fieldState }) => (
                <AutocompleteMultiById
                  options={effortContacts}
                  valueIds={field.value}
                  onChangeIds={field.onChange}
                  errorText={fieldState.error?.message}
                />
              )}
            />
          </FormControl>
          <FormControl>
            <FormLabel htmlFor="cc">CC</FormLabel>
            <Controller
              name="ccContacts"
              control={control}
              render={({ field, fieldState }) => (
                <AutocompleteMultiById
                  options={effortContacts}
                  valueIds={field.value}
                  onChangeIds={field.onChange}
                  errorText={fieldState.error?.message}
                />
              )}
            />
          </FormControl>
        </ResponsiveDialogContent>
        <ResponsiveDialogActions>
          <Button onClick={handleClose}>
            <FormattedMessage id="forgotPassword.cancel" defaultMessage="Vazgeç" />
          </Button>
          <Button variant="contained" type="submit">
            <FormattedMessage id="forgotPassword.continue" defaultMessage="Devam" />
          </Button>
        </ResponsiveDialogActions>
      </Box>
      <LoadingBackdrop open={loading} />
    </ResponsiveDialog>
  );
};

export default UpdateEffortStatusContactDialog;
